import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, ShieldCheck, AlertTriangle, Save, CreditCard } from "lucide-react";
import { toast } from "sonner";

export default function AdminStripeConfig() {
  const [config, setConfig] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/stripe-config");
      setConfig(data);
      setWebhookUrl(data.webhook_url || "");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not load"); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!apiKey.startsWith("sk_test_") && !apiKey.startsWith("sk_live_")) {
      toast.error("Stripe key must start with sk_test_ or sk_live_");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/stripe-config", { api_key: apiKey, webhook_url: webhookUrl });
      toast.success(`Saved · ${data.mode.toUpperCase()} mode`);
      setApiKey("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  if (!config) {
    return <div className="label-mono text-[#A3A3A3]">Loading…</div>;
  }

  const isLive = config.mode === "live";

  return (
    <div className="space-y-6" data-testid="admin-stripe-page">
      <div className="fade-up">
        <Link to="/admin" className="inline-flex items-center gap-2 text-xs text-[#A3A3A3] hover:text-white uppercase tracking-wider mb-3" data-testid="stripe-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to admin console
        </Link>
        <div className="label-mono mb-2" style={{ color: "#CCFF00" }}>PAYMENTS · STRIPE</div>
        <h1 className="h-display text-5xl tracking-tighter">Stripe configuration.</h1>
        <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl">
          Paste a fresh Stripe secret key to switch the platform between test and live mode. The platform reads from this stored config first, then falls back to the <code className="text-white">STRIPE_API_KEY</code> environment variable.
        </p>
      </div>

      {/* Current state */}
      <div className="card-surface p-5 fade-up d1 grid sm:grid-cols-3 gap-5">
        <div>
          <div className="label-mono mb-1">CURRENT KEY</div>
          <div className="font-mono text-sm text-white" data-testid="stripe-current-key">{config.api_key_masked || "—"}</div>
        </div>
        <div>
          <div className="label-mono mb-1">MODE</div>
          <div className="h-display text-2xl" style={{ color: isLive ? "#FF4500" : "#CCFF00" }} data-testid="stripe-mode">
            {config.mode?.toUpperCase()}
          </div>
        </div>
        <div>
          <div className="label-mono mb-1">SOURCE</div>
          <div className="text-sm">{config.source === "db" ? "Admin-saved" : "Environment variable"}</div>
        </div>
      </div>

      {/* Live-mode warning */}
      {isLive && (
        <div className="card-surface p-4 border-l-4 border-[#FF4500] fade-up d2 flex items-start gap-3" data-testid="stripe-live-warning">
          <AlertTriangle className="w-5 h-5 text-[#FF4500] shrink-0 mt-0.5" />
          <div className="text-xs text-[#E5E5E5]">
            <strong className="h-display text-base block mb-1">You&apos;re in LIVE mode — real money will be charged.</strong>
            Be careful — every booking checkout will charge the player&apos;s real card. To switch back to test mode, paste a <code>sk_test_…</code> key below.
          </div>
        </div>
      )}

      {/* Edit form */}
      <form onSubmit={save} className="card-surface p-6 fade-up d2 space-y-4" data-testid="stripe-form">
        <div className="label-mono flex items-center gap-2"><CreditCard className="w-3.5 h-3.5 text-[#CCFF00]" /> NEW STRIPE SECRET KEY</div>
        <div>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="sk_test_•••••••••••••••• or sk_live_••••••••••••••••"
            data-testid="stripe-key-input"
            className="w-full bg-[#141414] border border-[#262626] px-3 py-3 text-sm font-mono focus:outline-none focus:border-[#CCFF00]"
            required
          />
          <div className="text-[10px] text-[#525252] mt-2 uppercase tracking-wider">
            Find it in your Stripe Dashboard → Developers → API keys → Reveal secret key.
          </div>
        </div>

        <div>
          <div className="label-mono mb-2">WEBHOOK URL (OPTIONAL)</div>
          <input
            type="text"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://your-domain.com/api/webhook/stripe"
            data-testid="stripe-webhook-input"
            className="w-full bg-[#141414] border border-[#262626] px-3 py-3 text-sm font-mono focus:outline-none focus:border-[#CCFF00]"
          />
          <div className="text-[10px] text-[#525252] mt-2 uppercase tracking-wider">
            Defaults to the live host&apos;s /api/webhook/stripe — only override if Stripe expects a different URL.
          </div>
        </div>

        <div className="text-xs text-[#A3A3A3] border border-[#262626] p-3 flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 text-[#CCFF00] mt-0.5 shrink-0" />
          The full key is stored encrypted in the admin_config collection. The frontend only ever sees the masked form. This change is recorded in the audit log.
        </div>

        <button type="submit" disabled={saving} data-testid="stripe-save"
          className="btn-volt w-full py-3.5 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Stripe configuration"}
        </button>
      </form>
    </div>
  );
}
